import { httpClient } from "@/shared/http/httpClient";
import { AuthenticationError, AuthorizationError } from "@/shared/errors/auth-errors";
import { FrontendErrorsContractError } from "@/features/observability/api/frontend-errors.api";
import {
    platformErrorsContract,
    type PlatformErrorsEvents,
    type PlatformErrorsEventsPageParams,
    type PlatformErrorsGroups,
    type PlatformErrorsPopulationParams,
    type PlatformErrorsQueryParams,
    type PlatformErrorsSeries,
    type PlatformErrorsSummary,
} from "@/features/observability/platform-errors/types/platform-errors.types";

const API_PATH = "/api/v1/observability/platform-errors";

export type PlatformErrorsAccessAnswer = "allowed" | "forbidden" | "unavailable";

// A subclass so the shared resource hook classifies it as a contract failure like its frontend sibling.
export class PlatformErrorsContractError extends FrontendErrorsContractError {}

const baseQuery = (params: PlatformErrorsQueryParams): URLSearchParams => {
    const search = new URLSearchParams();
    if (params.environment !== null) search.set("environment", params.environment);
    search.set("from", String(params.from));
    search.set("to", String(params.to));
    if (params.service !== null) search.set("service", params.service);
    if (params.severity !== null) search.set("severity", params.severity);
    if (params.resourceType !== null) search.set("resourceType", params.resourceType);
    if (params.errorCode !== null) search.set("errorCode", params.errorCode);
    if (params.category !== null) search.set("category", params.category);
    return search;
};

// `/summary` is strict without asOf (it fixes its own); only the population reads take it.
const populationQuery = (params: PlatformErrorsPopulationParams): URLSearchParams => {
    const search = baseQuery(params);
    search.set("asOf", String(params.asOf));
    return search;
};

const eventsQuery = (params: PlatformErrorsEventsPageParams): URLSearchParams => {
    const search = populationQuery(params);
    search.set("limit", String(params.limit));
    if (params.fingerprint !== null) search.set("fingerprint", params.fingerprint);
    if (params.cursor !== null) search.set("cursor", params.cursor);
    return search;
};

const fetchValidated = async <T>(
    resource: string,
    search: URLSearchParams,
    guard: (value: unknown) => value is T,
    signal: AbortSignal,
): Promise<T> => {
    const body = await httpClient.getJson<unknown>(`${API_PATH}/${resource}?${search.toString()}`, undefined, signal);
    if (!guard(body)) throw new PlatformErrorsContractError(`The platform errors ${resource} response does not match the contract`);
    return body;
};

// UX only: the backend authorises every read. 204 is the one answer that shows the entry.
const access = async (signal: AbortSignal): Promise<PlatformErrorsAccessAnswer> => {
    try {
        const response = await httpClient.request(`${API_PATH}/access`, { method: "GET", signal });
        return response.status === 204 ? "allowed" : "unavailable";
    } catch (error) {
        if (error instanceof AuthorizationError || error instanceof AuthenticationError) return "forbidden";
        return "unavailable";
    }
};

const summary = (params: PlatformErrorsQueryParams, signal: AbortSignal): Promise<PlatformErrorsSummary> =>
    fetchValidated("summary", baseQuery(params), platformErrorsContract.isSummary, signal);

const series = (params: PlatformErrorsPopulationParams, signal: AbortSignal): Promise<PlatformErrorsSeries> =>
    fetchValidated("series", populationQuery(params), platformErrorsContract.isSeries, signal);

const groups = (params: PlatformErrorsPopulationParams, signal: AbortSignal): Promise<PlatformErrorsGroups> =>
    fetchValidated("groups", populationQuery(params), platformErrorsContract.isGroups, signal);

const events = (params: PlatformErrorsEventsPageParams, signal: AbortSignal): Promise<PlatformErrorsEvents> =>
    fetchValidated("events", eventsQuery(params), platformErrorsContract.isEvents, signal);

export const platformErrorsApi = { access, summary, series, groups, events };
